import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import {
  ComponentType, Overlay, OverlayConfig, OverlayContainer, OverlayRef, CdkScrollable, ScrollStrategy, CloseScrollStrategy, ConnectionPositionPair, NoopScrollStrategy
} from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector, TemplatePortal } from '@angular/cdk/portal';
import {
  ElementRef, EmbeddedViewRef, Inject, Injectable, InjectionToken, Injector, OnDestroy,
  Optional, SkipSelf, StaticProvider, TemplateRef, Host
} from '@angular/core';
import { defer, Observable, of as observableOf, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { PopoverConfig, Scroll } from './popover-config';
import { PopoverContainerComponent } from './popover-container/popover-container.component';
import { PopoverRef } from './popover-ref';



/** Injection token that can be used to access the data that was passed in to a popover. */
export const POPOVER_DATA = new InjectionToken<any>('finra-popover-data');

/** Injection token that can be used to specify default popover options. */
export const POPOVER_DEFAULT_OPTIONS =
  new InjectionToken<PopoverConfig>('finra-popover-default-options');

/** Injection token that determines the scroll handling while the popover is open. */
export const POPOVER_SCROLL_STRATEGY =
  new InjectionToken<() => ScrollStrategy>('finra-popover-scroll-strategy');

/** @docs-private */
export function POPOVER_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

/** @docs-private */
export function POPOVER_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay):
  () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

/** @docs-private */
export const POPOVER_SCROLL_STRATEGY_PROVIDER = {
  provide: POPOVER_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: POPOVER_SCROLL_STRATEGY_PROVIDER_FACTORY,
};

/** @docs-private */
export function POPOVER_DEFAULT_OPTIONS_FACTORY(): PopoverConfig {
  return new PopoverConfig();
}

@Injectable()
export class PopoverService implements OnDestroy {
  private openPopoversAtThisLevel: PopoverRef<any>[] = [];
  private readonly afterAllClosedAtThisLevel = new Subject<void>();
  private readonly afterOpenedAtThisLevel = new Subject<PopoverRef<any>>();
  private ariaHiddenElements = new Map<Element, string | null>();
  private scrollStrategy: () => ScrollStrategy;

  /**
   * Reference to the current popover in the view *at this level* (in the Angular injector tree).
   * If there is a parent popover service, all operations should delegate to that parent
   * via `openedPopoverRef`.
   */
  private popoverRefAtThisLevel: PopoverRef<any> | null = null;

  /** Reference to the currently opened popover at *any* level. */
  get openedPopoverRef(): PopoverRef<any> | null {
    // const parent = this.parentPopover;
    // return parent ? parent.openedPopoverRef : this.popoverRefAtThisLevel;
    return this.popoverRefAtThisLevel;
  }

  set openedPopoverRef(value: PopoverRef<any> | null) {
    // if (this.parentPopover) {
    //     this.parentPopover.openedPopoverRef = value;
    // } else {
    this.popoverRefAtThisLevel = value;
    // }
  }

  /** Keeps track of the currently-open popover. */
  get openPopovers(): PopoverRef<any>[] {
    return this.parentPopover ? this.parentPopover.openPopovers : this.openPopoversAtThisLevel;
  }

  /** Stream that emits when a popover has been opened. */
  get afterOpenedRef(): Subject<PopoverRef<any>> {
    return this.parentPopover ? this.parentPopover.afterOpenedRef : this.afterOpenedAtThisLevel;
  }

  get afterAllClosedRef(): Subject<void> {
    const parent = this.parentPopover;
    return parent ? parent.afterAllClosedRef : this.afterAllClosedAtThisLevel;
  }

  // TODO (jelbourn): tighten the typing right-hand side of this expression.
  /**
   * Stream that emits when all open popovers have finished closing.
   * Will emit on subscribe if there are no open popovers to begin with.
   */
  readonly afterAllClosed: Observable<void> = defer(() => this.openPopovers.length ?
    this.afterAllClosedRef :
    this.afterAllClosedRef.pipe(startWith(undefined))) as Observable<any>;

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private live: LiveAnnouncer,
    @Optional() @Inject(POPOVER_DEFAULT_OPTIONS) private defaultConfig: PopoverConfig,
    @Inject(POPOVER_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() @SkipSelf() private parentPopover: PopoverService,
    private overlayContainer: OverlayContainer) {
    this.scrollStrategy = scrollStrategy;
  }

  private attach<T>(
    content: ComponentType<T> | TemplateRef<T>,
    origin: ElementRef,
    userConfig?: PopoverConfig
  ): PopoverRef<T | EmbeddedViewRef<any>> {

    const config = { ...new PopoverConfig(), ...this.defaultConfig, ...userConfig };
    const overlayRef = this.createOverlay(origin, config);
    const container = this.attachPopoverContainer(overlayRef, origin, config);
    const popoverRef = new PopoverRef<T | EmbeddedViewRef<any>>(overlayRef, container, config);

    /*
      https://jira.finra.org/browse/RAMPDO-9283

      firefox treats the drag-drop items as a click event while other browsers don't.
      when that click event
    */
    const userAgent: string = window.navigator.userAgent.toLowerCase();
    const isFireFox = userAgent.includes(`firefox`);

    overlayRef.outsidePointerEvents().subscribe((event: MouseEvent) => {
      const isInsideOverlay = overlayRef.overlayElement.contains(event.target as Node);
      const isOutsideOverlay = !isInsideOverlay;
      const shouldIgnoreFirefoxEvent = (
        event.target === document.body
        && isFireFox
      );

      const shouldClose = (isOutsideOverlay && !shouldIgnoreFirefoxEvent);

      console.log({
        isOutsideOverlay,
        isInsideOverlay,
        event,
        overlayRef,
        popoverRef,
        config,
        container,
        content,
        origin,
        userAgent,
        isFireFox,
        shouldIgnoreFirefoxEvent,
        shouldClose
      });

      if (shouldClose) {
        popoverRef.close();
      }
    });

    if (content instanceof TemplateRef) {
      const portal = new TemplatePortal(content, null, {
          $implicit: config.data,
          popoverRef
      } as any);

      popoverRef.componentInstance = container.attachTemplatePortal(portal);
    } else {
      const injector = this.createInjector(config, popoverRef, container);
      const portal = new ComponentPortal(content, null, injector);
      const contentRef = container.attachComponentPortal(portal);

      popoverRef.componentInstance = contentRef.instance;
    }

    this.openedPopoverRef = popoverRef;
    return this.openedPopoverRef;
  }

  /**
   * Attaches the popover container component to the overlay.
   */
  private attachPopoverContainer(overlayRef: OverlayRef, origin: ElementRef, config: PopoverConfig) {

    const injector = new PortalInjector(this.injector, new WeakMap([[PopoverConfig, config]]));

    // const portalOutlet = new DomPortalOutlet(window.document.body, this.cfr, this.applicationRef, this.injector);
    const containerPortal = new ComponentPortal(PopoverContainerComponent, config.viewContainerRef, injector);
    const containerRef = overlayRef.attach(containerPortal);
    containerRef.instance.popoverConfig = config;
    containerRef.instance.origin = origin;

    return containerRef.instance;
  }

  /**
   * Opens a modal popover containing the given component.
   * @param componentOrTemplateRef Type of the component to load into the popover,
   *     or a TemplateRef to instantiate as the popover content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened popover.
   */
  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    origin: ElementRef,
    config?: PopoverConfig<D>): PopoverRef<EmbeddedViewRef<any> | T, any> {

    config = applyConfigDefaults(config, this.defaultConfig || new PopoverConfig());

    if (config.id && this.getPopoverById(config.id)) {
      throw Error(`Popover with id "${config.id}" exists already. The popover id must be unique.`);
    }

    const popoverRef = this.attach(componentOrTemplateRef, origin, config);

    // If this is the first popover that we're opening, hide all the non-overlay content.
    // if (!this.openPopovers.length) {
    //   this.hideNonPopoverContentFromAssistiveTechnology();
    // }

    this.openPopovers.push(popoverRef);
    popoverRef.afterClosed().subscribe(() => this.removeOpenPopover(popoverRef));
    this.afterOpenedRef.next(popoverRef);

    return popoverRef;
  }

  /**
   * Closes all of the currently-open popovers.
   */
  closeAll(): void {
    this.closePopovers(this.openPopovers);
  }

  /**
   * Finds an open popover by its id.
   * @param id ID to use when looking up the popover.
   */
  getPopoverById(id: string): PopoverRef<any> | undefined {
    return this.openPopovers.find(popover => popover.id === id);
  }

  ngOnDestroy() {
    // Only close the popovers at this level on destroy
    // since the parent service may still be active.
    this.closePopovers(this.openPopoversAtThisLevel);
    this.afterAllClosedAtThisLevel.complete();
    this.afterOpenedAtThisLevel.complete();
  }

  /**
   * Creates a new overlay and places it in the correct location.
   * @param config The user-specified popover config.
   */
  private createOverlay(origin: ElementRef, config: PopoverConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);

    const positionStrategy = this.overlay.position().flexibleConnectedTo(origin).withPositions([
      new ConnectionPositionPair({
        originX: config.horizontalPosition || 'start',
        originY: config.verticalPosition || 'bottom'
      },
        {
          overlayX: config.horizontalPosition || 'start',
          overlayY: config.verticalPosition === 'top' ? 'bottom' : config.verticalPosition === 'bottom' ? 'top' : config.verticalPosition
        }),
      new ConnectionPositionPair({
        originX: config.fallbackHorizontalPosition,
        originY: config.fallbackVerticalPosition
      },
        {
          overlayX: config.fallbackHorizontalPosition,
          overlayY: config.fallbackVerticalPosition === 'top' ? 'bottom' : config.fallbackVerticalPosition === 'bottom' ?
          'top' : config.fallbackVerticalPosition
        })
    ]).withPush(false);

    overlayConfig.positionStrategy = positionStrategy;
    return this.overlay.create(overlayConfig);
  }



  /**
   * Creates an overlay config from a popover config.
   * @param popoverConfig The popover configuration.
   * @returns The overlay configuration.
   */
  private getOverlayConfig(popoverConfig: PopoverConfig): OverlayConfig {
    let confirgScrollStrategy;
    if (popoverConfig.scroll === 'reposition' as Scroll) {
      confirgScrollStrategy = this.overlay.scrollStrategies.reposition();
    } else {
      confirgScrollStrategy = this.overlay.scrollStrategies.close();
    }

    const state = new OverlayConfig({
      panelClass: popoverConfig.panelClass,
      direction: popoverConfig.direction,
      disposeOnNavigation: popoverConfig.closeOnNavigation,
      hasBackdrop: false,
      scrollStrategy: confirgScrollStrategy
    });
    return state;
  }

  /**
   * Attaches an MatDialogContainer to a popover's already-created overlay.
   * @param overlay Reference to the popover's underlying overlay.
   * @param config The popover configuration.
   * @returns A promise resolving to a ComponentRef for the attached container.
   */
  private attachDialogContainer(overlay: OverlayRef, config: PopoverConfig): PopoverContainerComponent {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = Injector.create({
      parent: userInjector || this.injector,
      providers: [{ provide: PopoverConfig, useValue: config }]
    });

    const containerPortal = new ComponentPortal(PopoverContainerComponent,
      config.viewContainerRef, injector, config.componentFactoryResolver);
    const containerRef = overlay.attach<PopoverContainerComponent>(containerPortal);

    return containerRef.instance;
  }

  /**
   * Attaches the user-provided component to the already-created MatDialogContainer.
   * @param componentOrTemplateRef The type of component being loaded into the popover,
   *     or a TemplateRef to instantiate as the content.
   * @param popoverContainer Reference to the wrapping MatDialogContainer.
   * @param overlayRef Reference to the overlay in which the popover resides.
   * @param config The popover configuration.
   * @returns A promise resolving to the MatPopoverRef that should be returned to the user.
   */
  private attachPopoverContent<T, R>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    popoverContainer: PopoverContainerComponent,
    overlayRef: OverlayRef,
    config: PopoverConfig): PopoverRef<T, R> {

    // Create a reference to the popover we're creating in order to give the user a handle
    // to modify and close it.
    const popoverRef =
      new PopoverRef<T, R>(overlayRef, popoverContainer, config);

    if (componentOrTemplateRef instanceof TemplateRef) {
      popoverContainer.attachTemplatePortal(
        // tslint:disable-next-line
        new TemplatePortal<T>(componentOrTemplateRef, null!,
          { $implicit: config.data, PopoverRef } as any));
    } else {
      const injector = this.createInjector<T>(config, popoverRef, popoverContainer);
      const contentRef = popoverContainer.attachComponentPortal<T>(
        new ComponentPortal(componentOrTemplateRef, config.viewContainerRef, injector));
      popoverRef.componentInstance = contentRef.instance;
    }

    // popoverRef
    //   .updateSize(config.width, config.height)
    //   .updatePosition(config.position);

    return popoverRef;
  }

  /**
   * Creates a custom injector to be used inside the popover. This allows a component loaded inside
   * of a popover to close itself and, optionally, to return a value.
   * @param config Config object that is used to construct the popover.
   * @param PopoverRef Reference to the popover.
   * @param container Dialog container element that wraps all of the contents.
   * @returns The custom injector that can be used inside the popover.
   */
  private createInjector<T>(
    config: PopoverConfig,
    popoverRef: PopoverRef<T>,
    popoverContainer: PopoverContainerComponent): Injector {

    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

    // The PopoverContainerComponent is injected in the portal as the PopoverContainerComponent and the popover's
    // content are created out of the same ViewContainerRef and as such, are siblings for injector
    // purposes. To allow the hierarchy that is expected, the PopoverContainerComponent is explicitly
    // added to the injection tokens.
    const providers: StaticProvider[] = [
      { provide: PopoverContainerComponent, useValue: popoverContainer },
      { provide: POPOVER_DATA, useValue: config.data },
      { provide: PopoverRef, useValue: popoverRef }
    ];

    if (config.direction &&
      (!userInjector || !userInjector.get<Directionality | null>(Directionality, null))) {
      providers.push({
        provide: Directionality,
        useValue: { value: config.direction, change: observableOf() }
      });
    }

    return Injector.create({ parent: userInjector || this.injector, providers });
  }

  /**
   * Removes a popover from the array of open popovers.
   * @param PopoverRef Dialog to be removed.
   */
  private removeOpenPopover(popoverRef: PopoverRef<any>) {
    const index = this.openPopovers.indexOf(popoverRef);

    if (index > -1) {
      this.openPopovers.splice(index, 1);

      // If all the popovers were closed, remove/restore the `aria-hidden`
      // to a the siblings and emit to the `afterAllClosed` stream.
      if (!this.openPopovers.length) {
        this.ariaHiddenElements.forEach((previousValue, element) => {
          if (previousValue) {
            element.setAttribute('aria-hidden', previousValue);
          } else {
            element.removeAttribute('aria-hidden');
          }
        });

        this.ariaHiddenElements.clear();
        this.afterAllClosedRef.next();
      }
    }
  }

  /**
   * Hides all of the content that isn't an overlay from assistive technology.
   */
  private hideNonPopoverContentFromAssistiveTechnology() {
    const overlayContainer = this.overlayContainer.getContainerElement();

    // Ensure that the overlay container is attached to the DOM.
    if (overlayContainer.parentElement) {
      const siblings = overlayContainer.parentElement.children;

      for (let i = siblings.length - 1; i > -1; i--) {
        const sibling = siblings[i];

        if (sibling !== overlayContainer &&
          sibling.nodeName !== 'SCRIPT' &&
          sibling.nodeName !== 'STYLE' &&
          !sibling.hasAttribute('aria-live')) {

          this.ariaHiddenElements.set(sibling, sibling.getAttribute('aria-hidden'));
          sibling.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  /** Closes all of the popovers in an array. */
  private closePopovers(popovers: PopoverRef<any>[]) {
    let i = popovers.length;

    while (i--) {
      // The `_openDialogs` property isn't updated after close until the rxjs subscription
      // runs on the next microtask, in addition to modifying the array as we're going
      // through it. We loop through all of them and call close without assuming that
      // they'll be removed from the list instantaneously.
      popovers[i].close();
    }
  }

}

/**
 * Applies default options to the popover config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function applyConfigDefaults(
  config?: PopoverConfig, defaultOptions?: PopoverConfig): PopoverConfig {
  return { ...defaultOptions, ...config };
}
